import {BuildingData, BuildingReward} from "../models/remoteConstants.model";

export interface TimelineEntry {
    turn: number;
    // null when the reward did NOT fire on this turn; a number when it did.
    amount: number | null;
}

export interface TimelineResult {
    entries: TimelineEntry[];
    truncatedByMaxLevel: boolean;   // simulation stopped because Level cap reached
    truncatedByWindow: boolean;     // simulation stopped because we hit the maxTurns visualization cap
}

// Simulates the turn-by-turn behaviour of Building.OnTurnStarted +
// TickPeriodic + FireReward for a single cadence-based reward. Mirrors the
// runtime (Building.cs `InitializePassiveRewards`, `TickPeriodic`,
// `FireReward`) so the editor preview matches what actually happens in the
// client.
//
// Caveats:
//   - Only EveryNTurns and BuiltOnTile (which uses the same cadence shape)
//     return a non-empty timeline. Other kinds return [].
//   - Buildings with multiple periodic rewards share `MaxLevel` as a budget
//     in the runtime. This simulator treats the given reward as if it were
//     the only consumer of that budget — so a building with three EveryNTurns
//     rewards would in practice exhaust MaxLevel three times faster.
//   - Conditional triggers (BuiltOnTile placement check) are assumed satisfied.
export function computeRewardTimeline(
    reward: BuildingReward,
    building: BuildingData,
    maxTurns: number = 60,
): TimelineResult {
    let cadence: number;
    let firstTriggerDelay: number;
    switch (reward.trigger.kind) {
        case 'EveryNTurns':
        case 'BuiltOnTile':
            cadence = reward.trigger.cadence;
            firstTriggerDelay = reward.trigger.firstTriggerDelay;
            break;
        default:
            return {entries: [], truncatedByMaxLevel: false, truncatedByWindow: false};
    }
    if (cadence < 1) cadence = 1;

    const base = reward.grant.amount;
    const increase = reward.amountIncreasePerTrigger ?? 0;
    const maxLevel = building.maxLevel;
    const startLevel = building.startLevel;

    const entries: TimelineEntry[] = [];
    let count = Math.max(0, firstTriggerDelay) + 1;
    let timesTriggered = 0;
    let level = startLevel;
    let truncatedByMaxLevel = false;

    for (let turn = 1; turn <= maxTurns; turn++) {
        // AtMaxLevel check at the top of OnTurnStarted in the runtime
        if (maxLevel > 0 && level >= maxLevel) {
            truncatedByMaxLevel = true;
            break;
        }
        count--;
        let amount: number | null = null;
        if (count <= 0) {
            amount = base + timesTriggered * increase;
            timesTriggered++;
            if (maxLevel > 0) level++;
            count = cadence;
        }
        entries.push({turn, amount});
    }

    const truncatedByWindow = !truncatedByMaxLevel && (maxLevel === 0 || level < maxLevel);
    return {entries, truncatedByMaxLevel, truncatedByWindow};
}
